/* The rule the cockpit does not bend: a system identifier is never a label.
 *
 * `map/cases.ts`, `map/comms.ts`, `map/config.ts` and `map/customers.ts` each say
 * some version of it in a comment, because each of them resolves an id to a name
 * and has to explain what it does when the resolution fails. This file is the
 * same rule where it can be *applied* rather than restated — the one place that
 * knows what a uuid looks like, so a mapper can ask.
 *
 * The reason it needs asking at all is that a field being human-readable is not
 * something the schema can promise. `ServiceSpecification.specKey` is typed
 * `string` and is documented as a business key — `SSPEC-2210`, the thing an
 * operator quotes in a ticket — and some deployments populate it with a uuid
 * instead. Both are valid `string`s and only one of them is a label, so the
 * check is on the value, at the boundary, not on the type.
 *
 * Deliberately loose about the shape: any 8-4-4-4-12 run of hex matches, not
 * just the version and variant nibbles RFC 4122 permits. A non-conforming uuid
 * is every bit as unreadable as a conforming one, and nothing a person would
 * willingly type as a key — `SSPEC-2210`, `BR-FIN-001`, `Access v3` — comes near
 * the pattern. False negatives here put a uuid on a screen; there are no false
 * positives worth trading for that.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether this string is a uuid, and so must not be shown to anybody. */
export function isUuid(value: string | null | undefined): boolean {
  return typeof value === 'string' && UUID.test(value.trim());
}

/** The value if a reader could quote it, otherwise `undefined`.
 *
 * Returns `undefined` rather than a dash so it chains: a caller writes its own
 * ladder of candidates with `??` and decides at the end what "none of these" is
 * called, which differs by screen. Blank and whitespace-only collapse to the
 * same nothing as a uuid — an empty cell and a uuid are both a field that failed
 * to produce a name, and the caller's fallback is the answer to both. */
export function readable(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' || isUuid(trimmed) ? undefined : trimmed;
}
